import { Component, OnInit, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CounselorNav } from '../counselor-nav/counselor-nav';
import { UserAvatar } from '../../../common/user-avatar/user-avatar';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { HasPermissionDirective } from '../../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../../constants/permissions';
import { GroupDialog, GroupDialogData } from './group-dialog/group-dialog';
import { ConfirmCancelDialog } from '../../../common/confirm-cancel-dialog/confirm-cancel-dialog';

import {
  StudentService,
  CounselorStudent,
  GRADE_LABELS,
  deriveStatus,
} from '../../../../services/student.service';
import { GroupService } from '../../../../services/group.service';
import { Group } from '../../../../models/group';
import { User } from '../../../../models/user';

type GradeFilter = 'all' | 9 | 10 | 11 | 12;
type StatusFilter = 'all' | 'on-track' | 'needs-attention' | 'new';
type PageView = 'students' | 'groups';

@Component({
  selector: 'app-my-students',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatDialogModule,
    CounselorNav,
    UserAvatar,
    HasPermissionDirective,
    MatCheckboxModule,
  ],
  templateUrl: './counselor-students.html',
  styleUrls: ['./counselor-students.css'],
})
export class CounselorStudents implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  loading = signal(true);
  error = signal<string | null>(null);
  selectedStudent = signal<CounselorStudent | null>(null);
  gradeFilter = signal<GradeFilter>('all');
  statusFilter = signal<StatusFilter>('all');
  searchValue = signal('');
  selectedGroupIds = signal<Set<number>>(new Set());

  private allStudents = signal<CounselorStudent[]>([]);

  view = signal<PageView>('students');
  groups = signal<Group[]>([]);
  groupsLoading = signal(true);
  groupsError = signal<string | null>(null);
  selectedGroup = signal<Group | null>(null);
  groupSearchValue = signal('');
  pendingAddStudentId: number | null = null;

  permissions = USER_PERMISSIONS;

  hasSelection = computed(() => this.selectedGroupIds().size > 0);
  filteredStudents = computed<CounselorStudent[]>(() => {
    const search = this.searchValue().toLowerCase();
    const grade = this.gradeFilter();
    const status = this.statusFilter();

    return this.allStudents().filter((s) => {
      const fullName =
        `${s.user.preferred_name || s.user.first_name} ${s.user.last_name}`.toLowerCase();
      const career = (s.Profile?.Career?.name ?? '').toLowerCase();
      const matchSearch = !search || fullName.includes(search) || career.includes(search);
      const matchGrade = grade === 'all' || s.grade_level === grade;
      const matchStatus = status === 'all' || this.deriveStatus(s) === status;
      return matchSearch && matchGrade && matchStatus;
    });
  });

  needsAttentionStudents = computed<CounselorStudent[]>(() =>
    this.filteredStudents().filter((s) => this.deriveStatus(s) === 'needs-attention'),
  );

  filteredGroups = computed<Group[]>(() => {
    const term = this.groupSearchValue().trim().toLowerCase();
    if (!term) return this.groups();
    return this.groups().filter((g) => g.name.toLowerCase().includes(term));
  });

  private studentByUserId = computed(() => {
    const map = new Map<number, CounselorStudent>();
    this.allStudents().forEach((s) => map.set(s.user.id, s));
    return map;
  });

  readonly gradeOptions: { val: GradeFilter; label: string }[] = [
    { val: 'all', label: 'All grades' },
    { val: 9, label: 'Freshman' },
    { val: 10, label: 'Sophomore' },
    { val: 11, label: 'Junior' },
    { val: 12, label: 'Senior' },
  ];

  readonly statusOptions: { val: StatusFilter; label: string }[] = [
    { val: 'all', label: 'All' },
    { val: 'on-track', label: 'On track' },
    { val: 'needs-attention', label: 'Needs attention' },
    { val: 'new', label: 'New' },
  ];

  constructor(
    private studentService: StudentService,
    private groupService: GroupService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    const requestedView = this.route.snapshot.queryParamMap.get('view');
    if (requestedView === 'groups') {
      this.view.set('groups');
    }

    this.loadStudents();
    this.loadGroups();
  }

  private loadStudents(): void {
    this.studentService.getMyStudents().subscribe({
      next: (students) => {
        this.allStudents.set(students);
        this.loading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.error.set('Failed to load students. Please try again.');
        this.loading.set(false);
      },
    });
  }

  loadGroups(): void {
    this.groupsLoading.set(true);
    this.groupService.getManagedGroups().subscribe({
      next: (groups) => {
        this.groups.set(groups);
        this.groupsLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.groupsError.set('Failed to load groups. Please try again.');
        this.groupsLoading.set(false);
      },
    });
  }

  setView(v: PageView): void {
    this.view.set(v);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: v === 'groups' ? 'groups' : null },
      queryParamsHandling: 'merge',
    });
  }

  applySearch(event: Event): void {
    this.searchValue.set((event.target as HTMLInputElement).value.trim());
  }

  clearSearch(): void {
    this.searchValue.set('');
  }

  setGradeFilter(val: GradeFilter): void {
    this.gradeFilter.set(val);
  }

  setStatusFilter(val: StatusFilter): void {
    this.statusFilter.set(val);
  }

  openDetail(student: CounselorStudent): void {
    this.selectedStudent.set(student);
  }

  closeDetail(): void {
    this.selectedStudent.set(null);
  }

  applyGroupSearch(event: Event): void {
    this.groupSearchValue.set((event.target as HTMLInputElement).value.trim());
  }

  openGroupDetail(group: Group): void {
    this.router.navigate(['/counselors/students/groups', group.id]);
  }

  closeGroupDetail(): void {
    this.selectedGroup.set(null);
    this.pendingAddStudentId = null;
  }

  groupSourceLabel(group: Group): string {
    return group.source === 'oneroster' ? 'Synced from OneRoster' : 'Custom group';
  }

  openCreateGroup(): void {
    const ref = this.dialog.open<GroupDialog, GroupDialogData>(GroupDialog, {
      width: '520px',
      maxWidth: '95vw',
      data: { students: this.allStudents() },
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.created) {
        this.loadGroups();
      }
    });
  }

  openEditGroup(group: Group, event?: Event): void {
    event?.stopPropagation();
    const ref = this.dialog.open<GroupDialog, GroupDialogData>(GroupDialog, {
      width: '520px',
      maxWidth: '95vw',
      data: { group, students: this.allStudents() },
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.updated) {
        this.loadGroups();
      }
    });
  }

  initials(s: CounselorStudent): string {
    return `${(s.user.preferred_name || s.user.first_name)[0]}${s.user.last_name[0]}`.toUpperCase();
  }

  gradeLabel(level: number): string {
    return GRADE_LABELS[level] ?? `Grade ${level}`;
  }

  deriveStatus(s: CounselorStudent): 'on-track' | 'needs-attention' | 'new' {
    return deriveStatus(s.Profile?.profile_complete_percent ?? 0);
  }

  completionColor(pct: number): 'primary' | 'accent' | 'warn' {
    if (pct >= 75) return 'primary';
    if (pct >= 40) return 'accent';
    return 'warn';
  }

  avatarClass(s: CounselorStudent): string {
    const classes = [
      'cd-avatar-orange',
      'cd-avatar-navy',
      'cd-avatar-teal',
      'cd-avatar-purple',
      'cd-avatar-rust',
      'cd-avatar-slate',
    ];
    return classes[s.id % classes.length];
  }

  isSelected(id: number): boolean {
    return this.selectedGroupIds().has(id);
  }

  toggleGroupSelection(id: number, event: Event): void {
    event.stopPropagation();
    const next = new Set(this.selectedGroupIds());
    next.has(id) ? next.delete(id) : next.add(id);
    this.selectedGroupIds.set(next);
    console.log(this.selectedGroupIds());
  }

  clearSelection(): void {
    this.selectedGroupIds.set(new Set());
  }
  deleteSelected(): void {
    const ids = Array.from(this.selectedGroupIds());

    const selectedGroups = this.filteredGroups().filter((g) => ids.includes(g.id));
    const groupNames = selectedGroups.map((g) => g.name).join(', ');

    const data = {
      title: 'Confirm Delete Groups',
      content: `Are you sure you want to delete ${ids.length} group${ids.length !== 1 ? 's' : ''} (${groupNames})? ${ids.length !== 1 ? 'These groups' : 'This group'} will be removed for all members.`,
      confirmLabel: 'Delete',
      icon: 'delete',
    };

    const dialogRef = this.dialog.open(ConfirmCancelDialog, { data });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      const deletions = ids.map((id) => this.groupService.removeGroup(id));

      forkJoin(deletions).subscribe({
        next: () => {
          this.clearSelection();
          this.loadGroups();
        },
        error: () => {
          this.groupsError.set('Failed to delete one or more groups.');
        },
      });
    });
  }
}
